import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  Settings,
} from 'lucide-react';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { PortalImportModal } from './PortalImportModal';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { usePortalioStatus } from '@/hooks/usePortalioStatus';
import { cn } from '@/lib/utils';

interface PortalioButtonProps {
  floorPlanId: string;
}

export function PortalioButton({ floorPlanId }: PortalioButtonProps) {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { data: floorPlanData } = useFloorPlanContext();
  const setActiveDrawer = useAppStore((state) => state.setActiveDrawer);
  const { status: portalioStatus, isLoading: statusLoading } = usePortalioStatus();
  const [importedProductsCount, setImportedProductsCount] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Derive connection state from Portal.io status
  const isConnected = portalioStatus === 'connected';

  // Load imported products count when connected
  // Re-load when floor plan data changes (e.g., after unlinking)
  useEffect(() => {
    if (organization?.id && isConnected && floorPlanData?.floorPlan?.id) {
      loadImportedProductsCount();
    }
  }, [organization?.id, isConnected, floorPlanData?.floorPlan?.id, floorPlanData?.lvProducts]);

  // Listen for proposal unlink events
  useEffect(() => {
    const handleProposalUnlinked = () => {
      // Immediately set count to 0 when proposal is unlinked
      setImportedProductsCount(0);
      logger.log('Portal proposal unlinked - clearing product count');
    };

    window.addEventListener('portal-proposal-unlinked', handleProposalUnlinked);
    return () => {
      window.removeEventListener('portal-proposal-unlinked', handleProposalUnlinked);
    };
  }, []);


  const loadImportedProductsCount = async () => {
    if (!organization?.id || !floorPlanData?.floorPlan?.id) return;

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { count, error } = await supabase
        .from('portal_imported_products')
        .select('*', { count: 'exact', head: true })
        .eq('clerk_org_id', organization.id)
        .eq('floor_plan_id', floorPlanData.floorPlan.id);

      if (error) {
        logger.error('Error querying imported products:', error);
        return;
      }

      setImportedProductsCount(count || 0);
    } catch (error) {
      logger.error('Error loading imported products count:', error);
    }
  };

  const handleMainButtonClick = () => {
    // If products exist, open the drawer; otherwise, open import modal
    if (importedProductsCount > 0) {
      setActiveDrawer('portalProducts');
    } else {
      setShowImportModal(true);
    }
  };

  const handleProductsImported = () => {
    // Reload the imported products count
    loadImportedProductsCount();
    toast.success('Products imported successfully');
  };

  const handleOpenSettings = () => {
    navigate('/integrations/portal');
  };

  // Don't render if still loading
  if (statusLoading) {
    return null;
  }

  // Show button always, but styled differently based on connection and products
  const hasProducts = importedProductsCount > 0;
  
  return (
    <>
      <div className="flex items-center">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-8 rounded-r-none border-r-0 hover:bg-accent/50 px-2",
            !isConnected || !hasProducts ? "opacity-50" : ""
          )}
          onClick={portalioStatus === 'connected' ? handleMainButtonClick : handleOpenSettings}
          disabled={portalioStatus === 'loading'}
        >
          <img 
            src="/portal-logo.png" 
            alt="Portal.io" 
            className={cn(
              "h-4 w-4 mr-1.5",
              !hasProducts ? "grayscale" : ""
            )}
          />
          <span className={cn(
            "text-xs font-medium",
            !hasProducts ? "text-muted-foreground" : ""
          )}>
            Portal.io
          </span>
          {hasProducts && (
            <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
              {importedProductsCount}
            </Badge>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-1.5 rounded-l-none hover:bg-accent/50"
            >
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Portal.io Integration</span>
              <Badge 
                variant={
                  portalioStatus === 'connected' ? 'default' :
                  portalioStatus === 'expired' ? 'warning' :
                  portalioStatus === 'disconnected' ? 'destructive' :
                  'outline'
                } 
                className={cn(
                  "text-xs",
                  portalioStatus === 'connected' && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800",
                  portalioStatus === 'expired' && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 border-orange-200 dark:border-orange-800"
                )}
              >
                {portalioStatus === 'connected' ? 'Connected' :
                 portalioStatus === 'expired' ? 'Token Expired' :
                 portalioStatus === 'disconnected' ? 'Disconnected' :
                 portalioStatus === 'not_configured' ? 'Not Configured' :
                 'Loading...'}
              </Badge>
            </DropdownMenuLabel>
            
            <DropdownMenuSeparator />
            
            <div className="px-2 py-2">
              <p className="text-sm text-muted-foreground">
                {portalioStatus === 'connected' ? (
                  importedProductsCount > 0 ? (
                    <>
                      <span className="font-medium text-foreground">{importedProductsCount}</span> products imported
                    </>
                  ) : (
                    'No products imported yet'
                  )
                ) : portalioStatus === 'expired' ? (
                  'Token expired - reconnect in settings'
                ) : portalioStatus === 'disconnected' ? (
                  'Connect to import products'
                ) : portalioStatus === 'not_configured' ? (
                  'Configure Portal.io integration'
                ) : (
                  'Loading...'
                )}
              </p>
            </div>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={handleOpenSettings}>
              <Settings className="h-4 w-4 mr-2" />
              Portal.io Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Import Modal */}
      <PortalImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onProductsImported={handleProductsImported}
      />
    </>
  );
}
